import { z } from 'zod';

import { CashMovementKindSchema } from './cash-movement.schema.js';
import {
  CashDenominationSchema,
  CashOpeningModeSchema,
  CashSessionCloseModeSchema,
} from './cash-sessions.schema.js';
import {
  cashReportLocalDateSchema,
  CashReportCashLocationRefDtoSchema,
  CashReportMovementCategorySchema,
  CashReportSessionRefDtoSchema,
} from './cash-report-common.schema.js';
import type {
  CashDailyForceUnverifiedAuditRiskRowDto,
  CashDailyPhysicalActivityTotalsDto,
  CashDailySummaryClosedSessionRowDto,
  CashDailySummaryOpenSessionRowDto,
} from './cash-report-daily.dto.js';

export const CashDailySummaryClosedSessionRowDtoSchema: z.ZodType<CashDailySummaryClosedSessionRowDto> =
  z.object({
    localDate: cashReportLocalDateSchema,
    timezone: z.string().trim().min(1),
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    cashLocation: CashReportCashLocationRefDtoSchema,
    session: CashReportSessionRefDtoSchema,
    openingMode: CashOpeningModeSchema,
    openingBasisAmountMinor: z.number().int(),
    openingCountedAmountMinor: z.number().int().nullable(),
    closeMode: CashSessionCloseModeSchema,
    closeExpectedCashMinor: z.number().int().nullable(),
    closeCountedCashMinor: z.number().int().nullable(),
    closeVarianceAmountMinor: z.number().int().nullable(),
    closingDenominations: z.array(CashDenominationSchema).nullable().optional(),
    auditRisk: z.boolean(),
    isLive: z.literal(false),
  });

export const CashDailySummaryOpenSessionRowDtoSchema: z.ZodType<CashDailySummaryOpenSessionRowDto> =
  z.object({
    localDate: cashReportLocalDateSchema,
    timezone: z.string().trim().min(1),
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    cashLocation: CashReportCashLocationRefDtoSchema,
    session: CashReportSessionRefDtoSchema,
    openingMode: CashOpeningModeSchema,
    openingBasisAmountMinor: z.number().int(),
    openingCountedAmountMinor: z.number().int().nullable(),
    currentExpectedCashMinor: z.number().int(),
    auditRisk: z.boolean(),
    isLive: z.literal(true),
  });

export const CashDailyPhysicalActivityTotalsDtoSchema: z.ZodType<CashDailyPhysicalActivityTotalsDto> =
  z.object({
    localDate: cashReportLocalDateSchema,
    timezone: z.string().trim().min(1),
    cashLocation: CashReportCashLocationRefDtoSchema,
    reportCategory: CashReportMovementCategorySchema,
    movementKind: CashMovementKindSchema.nullable(),
    totalInMinor: z.number().int().nonnegative(),
    totalOutMinor: z.number().int().nonnegative(),
    netAmountMinor: z.number().int(),
    movementCount: z.number().int().nonnegative(),
  });

export const CashDailyForceUnverifiedAuditRiskRowDtoSchema: z.ZodType<CashDailyForceUnverifiedAuditRiskRowDto> =
  z.object({
    localDate: cashReportLocalDateSchema,
    timezone: z.string().trim().min(1),
    cashLocation: CashReportCashLocationRefDtoSchema,
    session: CashReportSessionRefDtoSchema,
    closeExpectedCashMinor: z.number().int().nullable(),
    closeCountedCashMinor: z.null(),
    closeVarianceAmountMinor: z.null(),
    auditRisk: z.literal(true),
  });
